import React, { useMemo } from 'react';
import { graphql, Link } from 'gatsby';
import get from 'lodash/get';
import Card from '../components/PolicyPageCard';
import NewsCard from '../components/NewsCard';

import {
  CardContainer,
  TagButtonGroup,
  Title,
  MainContainer,
  PostContainer,
  HyperlinkCategories,
} from '../styles/news.template.styled';
import SEO from '../components/seo';
import CustomPagination from '../components/customPagination';
import SignUpBanner from '../components/SignUpBanner';
import BannerGatsbyImg from '../components/BannerGatsbyImg';
import { getPrevNextPage } from '../utils/helper';

export const Head = ({ data, pageContext }) => {
  const { page } = pageContext;
  const baseUrl = `/${page}/${pageContext.slug}/`;
  const currentPage = pageContext?.currentPage;
  const totalPages = pageContext?.totalCategoriesPages;
  const pageQueryName = get(pageContext, 'page');
  const activeCategory = get(pageContext, 'category');
  const title =
    pageQueryName === 'news'
      ? `${
          activeCategory ? `${activeCategory} |` : ''
        } News | Pandemic Action Network`
      : `${activeCategory} | Pandemic Action Network`;

  const { prevPage, nextPage } = getPrevNextPage(
    baseUrl,
    currentPage,
    totalPages,
  );

  const canonicalUrl = pageQueryName === 'news' ? '' : `${get(pageContext, 'canonicalUrl')}${get(pageContext, 'slug')}/`;

  const seoData = {
    title,
    description: get(data, 'allWpPage.nodes[0].seo.metaDesc'),
    image: get(data, 'allWpPage.nodes[0].seo.opengraphImage.mediaItemUrl'),
    pageUrl: get(data, 'allWpPage.nodes[0].seo.opengraphUrl'),
    prevPage,
    nextPage,
    slug: currentPage === 1 ? baseUrl : `${baseUrl}${currentPage}/`,
    canonicalUrl,
  };

  return <SEO seoProps={seoData} />;
};

const News = (props) => {
  const allNews = props.data.allWpPost.nodes;
  const allPolicy = props.data.allWpPolicy.nodes;
  const currentPage = props.pageContext.currentPage;
  const totalPages = props.pageContext.totalCategoriesPages;
  const activeCategory = props.pageContext.category;
  const page = props.pageContext.page;

  const baseUrl = `/${page}/${props.pageContext.slug}/`;

  const bannerImgUrl = get(
    props,
    'data.allWpPage.nodes[0].featuredImage.node.localFile',
  );
  const bannerImg = get(
    props,
    'data.allWpPage.nodes[0].policiesAndActionsPage.backgroundImage.localFile',
  );
  const bannerNewsTitle = get(
    props,
    'data.allWpPage.nodes[0].newsPageFields.bannerTitle',
  );
  const bannerPolicyTitle = get(
    props,
    'data.allWpPage.nodes[0].policiesAndActionsPage.policiesAndActionTitle',
  );
  const content = get(props, 'data.allWpPage.nodes[0].content');

  const pageQueryName = get(props, 'pageContext.page');
  const allPolicyCategories = get(props, 'data.allWpPolicyCategory.edges');
  const categories = get(props, 'data.allWpCategory.nodes');

  const primaryCategories = categories
    .filter((val) => val.name === 'Primary Category')
    .map((val) => val.wpChildren.nodes);
  const secondaryCategories = categories
    .filter((val) => (val.name === 'Secondary Category' ? val : null))
    .map((val) => {
      return val.wpChildren.nodes;
    });

  const pagination = useMemo(() => {
    return (
      <CustomPagination
        baseUrl={baseUrl}
        currentPage={currentPage}
        totalPages={totalPages}
        sectionId='#policy-title'
      />
    );
  }, [currentPage]);

  return (
    <>
      <BannerGatsbyImg
        title={pageQueryName === 'news' ? bannerNewsTitle : bannerPolicyTitle}
        image={bannerImgUrl || bannerImg}
      />
      <div className='wrapper'>
        <MainContainer
          id={pageQueryName === 'news' ? 'new-list-page' : 'policy-list-page'}
        >
          {pageQueryName !== 'news' && (
            <div
              className='policy-content'
              dangerouslySetInnerHTML={{ __html: content }}
            />
          )}
          <Title id='policy-title'>Categories</Title>
          <TagButtonGroup>
            <li>
              <Link
                to={
                  pageQueryName === 'news'
                    ? `/news/`
                    : `/policies-and-actions/#policy-title`
                }
                title='All'
              >
                All
              </Link>
            </li>
            {pageQueryName === 'news'
              ? Array.isArray(primaryCategories) &&
                primaryCategories &&
                primaryCategories[0].map((category, index) => {
                  return (
                    <li key={index}>
                      <Link
                        to={`/news/${category.slug}/`}
                        className={
                          activeCategory === category.name ? 'active' : null
                        }
                        title={category.name}
                      >
                        {category.name}
                      </Link>
                    </li>
                  );
                })
              : allPolicyCategories.map((category, index) => {
                  if (!Object.keys(category.node).length) return null;

                  return (
                    <li key={index}>
                      <Link
                        to={`/policies-and-actions/${category.node.slug}/#policy-title`}
                        className={
                          activeCategory === category.node.name
                            ? 'active'
                            : null
                        }
                        title={category.node.name}
                      >
                        {category.node.name}
                      </Link>
                    </li>
                  );
                })}
          </TagButtonGroup>
          {pageQueryName === 'news' ? (
            <HyperlinkCategories>
              {Array.isArray(secondaryCategories) &&
                secondaryCategories &&
                secondaryCategories[0].map((category, index) => {
                  return (
                    <li className='link-item' key={index}>
                      <Link
                        to={`/news/${category.slug}/`}
                        className={
                          activeCategory === category.name ? 'active' : null
                        }
                        title={category.name}
                      >
                        {category.name}
                      </Link>
                    </li>
                  );
                })}
            </HyperlinkCategories>
          ) : null}

          {pageQueryName === 'news' ? (
            <>
              <CardContainer>
                {allNews.map((news, index) => {
                  return (
                    <li key={news.id} className='news'>
                      <NewsCard {...news} noContent={true} />
                    </li>
                  );
                })}
              </CardContainer>
            </>
          ) : (
            <>
              <PostContainer secondary className='desktop-container'>
                {allPolicy.map((policy, index) => {
                  return <Card policy={policy} key={index} noContent />;
                })}
              </PostContainer>
              <PostContainer secondary className='mobile-container'>
                {allPolicy.map((policy, index) => {
                  return (
                    <>
                      {Array.isArray(allPolicy) &&
                        allPolicy.length > 8 &&
                        index === allPolicy.length / 2 && (
                          <li className='sign-up-banner'>
                            <SignUpBanner />
                          </li>
                        )}
                      <Card policy={policy} key={index} noContent />
                    </>
                  );
                })}
              </PostContainer>
            </>
          )}
          {totalPages > 1 && pagination}
        </MainContainer>
      </div>
    </>
  );
};
export default News;

export const pageQuery = graphql`
  query NewListingQuery(
    $skip: Int!
    $limit: Int!
    $category: String
    $page: String
  ) {
    allWpPost(
      limit: $limit
      skip: $skip
      sort: { date: DESC }
      filter: {
        categories: { nodes: { elemMatch: { name: { eq: $category } } } }
      }
    ) {
      nodes {
        title
        id
        slug
        content
        postsFields {
          postSubTitle
          author {
            name
            slug
            id
          }
        }
        featuredImage {
          node {
            altText
            mediaItemUrl
            localFile {
              childImageSharp {
                gatsbyImageData(
                  placeholder: BLURRED
                  width: 1366
                  height: 768
                  formats: WEBP
                )
              }
            }
          }
        }
        author {
          node {
            name
            slug
            id
          }
        }
        date
        categories {
          nodes {
            name
            id
            slug
          }
        }
      }
    }
    allWpCategory {
      nodes {
        name
        slug
        posts {
          nodes {
            id
            slug
          }
        }
        wpChildren {
          nodes {
            name
            slug
          }
        }
      }
    }
    allWpPolicyCategory {
      edges {
        node {
          name
          slug
        }
      }
    }

    allWpPolicy(
      limit: $limit
      skip: $skip
      filter: {
        policyCategories: { nodes: { elemMatch: { name: { eq: $category } } } }
      }
      sort: { date: DESC }
    ) {
      nodes {
        content
        title
        id
        slug
        date
        featuredImage {
          node {
            altText
            mediaItemUrl
            localFile {
              childImageSharp {
                gatsbyImageData(
                  placeholder: BLURRED
                  width: 1366
                  height: 768
                  formats: WEBP
                )
              }
            }
          }
        }
        policyPostsFields {
          policySubTitle
          policyAuthor {
            name
            slug
          }
        }
      }
    }
    allWpPage(filter: { slug: { eq: $page } }) {
      nodes {
        title
        content
        id
        seo {
          metaDesc
          title
          opengraphImage {
            mediaItemUrl
          }
          opengraphUrl
        }
        featuredImage {
          node {
            altText
            mediaItemUrl
            localFile {
              childImageSharp {
                gatsbyImageData(placeholder: BLURRED, formats: WEBP)
              }
            }
          }
        }
        policiesAndActionsPage {
          backgroundImage {
            mediaItemUrl
            localFile {
              childImageSharp {
                gatsbyImageData(placeholder: BLURRED, formats: WEBP)
              }
            }
          }
          policiesAndActionTitle
        }
        newsPageFields {
          bannerTitle
        }
      }
    }
  }
`;
